import React from 'react';
import Switch from '@material-ui/core/Switch';
import { withStyles } from '@material-ui/core/styles';

/**
 * WordMatchToggle — a custom-styled Material UI Switch.
 *
 * How MUI's `withStyles` works here:
 *  - `withStyles` is a Higher-Order Component (HOC) that injects a `classes` object.
 *  - Each key in the style object (root, switchBase, thumb, track, checked) becomes
 *    a generated CSS class name available on `classes.<key>`.
 *  - We pass those class names to the underlying <Switch> via its `classes` prop,
 *    which tells MUI which internal element to apply each class to.
 *
 * The `$` syntax (e.g. `&$checked`) is a JSS feature for referencing another
 * class in the same stylesheet — it resolves to the generated class for `checked`.
 */

// --- Style overrides for the switch anatomy ---

const toggleStyles = (theme) => ({
  // The outer container of the switch
  root: {
    width: 40,
    height: 24,
    padding: 0,
  },

  // The clickable base that slides left/right (houses the thumb)
  switchBase: {
    padding: 1,
    color: '#676B80', // thumb color when OFF

    // When the switch is ON (`checked` class is applied)
    '&$checked': {
      transform: 'translateX(16px)', // slide the thumb to the right
      color: '#FFFFFF', // thumb color when ON

      // Style the track (background rail) when ON
      '& + $track': {
        backgroundColor: '#254B86',
        opacity: 1,
        border: 'none',
      },
    },

    '&:hover': {
      backgroundColor: 'transparent', // no hover highlight circle
    },
  },

  // The circular draggable knob
  thumb: {
    width: 18,
    height: 18,
    position: 'relative',
    top: 2,
    left: 2,
    boxShadow: 'none',
    borderRadius: '50%',
  },

  // The background rail behind the thumb
  track: {
    borderRadius: 12,
    border: '1px solid #676B80',
    backgroundColor: '#EFF1FF', // light purple-ish when OFF
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },

  // Empty rule — required so `&$checked` can reference it (JSS needs
  // the class to exist in the stylesheet to generate a selector for it)
  checked: {},
});

/**
 * Render function that maps the generated `classes` to Switch internals.
 * All remaining props (checked, onChange, name, inputProps, etc.)
 * are spread onto the underlying <Switch>.
 */
const WordMatchToggle = withStyles(toggleStyles)(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

export default WordMatchToggle;
